import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  Bot,
  ChevronDown,
  ChevronUp,
  History,
  Loader2,
  MessageSquare,
  RotateCcw,
  Save,
  Send,
  Trash2,
  User,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import { Separator } from '../../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import AgentPageShell from '../../components/agent/AgentPageShell';
import ConfidenceBar from '../../components/agent/ConfidenceBar';
import { cn } from '../../lib/utils';
import {
  fetchConfig,
  fetchConfigVersions,
  publishConfig,
  restoreConfigVersion,
  saveConfig,
  sendChatMessage,
} from '../../lib/agentApi';
import { formatWhen } from './AgentDashboardPage';

const TONES = ['Formal', 'Balanced', 'Friendly', 'Playful'];
const LENGTHS = ['Concise', 'Detailed', 'Adaptive'];
const PROMPT_CHAR_LIMIT = 8000;

const RULES = [
  { key: 'cite_sources', title: 'Always cite source documents', desc: 'Every factual claim links back to the document it came from.' },
  { key: 'no_speculation', title: 'Avoid speculation without sources', desc: 'If no document covers it, the agent says so instead of guessing.' },
  { key: 'escalate_after_failures', title: 'Offer escalation after repeated failures', desc: 'Suggests a human handoff when it cannot help.' },
  { key: 'clarify_first', title: 'Ask clarifying questions first', desc: 'Seeks context before answering an ambiguous question.' },
  { key: 'markdown', title: 'Support markdown in responses', desc: 'Allows lists, tables, bold and code blocks in output.' },
  { key: 'docs_only', title: 'Limit to documentation only', desc: 'Blocks the model from using general knowledge outside your spaces.' },
];

const PromptPersonaPage = () => (
  <AgentPageShell
    title="Custom Prompt & Persona"
    description="Define this agent's identity, tone and behaviour rules"
    icon={MessageSquare}
    breadcrumb={['Prompt & Persona']}
  >
    {({ agent, orgId, refresh }) => (
      <PersonaBody key={agent.id} agent={agent} orgId={orgId} refreshAgents={refresh} />
    )}
  </AgentPageShell>
);

const PersonaBody = ({ agent, orgId, refreshAgents }) => {
  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchConfig(orgId, agent.id);
      setConfig(result.config);
      setMeta(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load configuration');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id]);

  const loadVersions = useCallback(async () => {
    try {
      const result = await fetchConfigVersions(orgId, agent.id);
      setVersions(result.versions || []);
    } catch {
      setVersions([]);
    }
  }, [orgId, agent.id]);

  useEffect(() => {
    load();
    loadVersions();
  }, [load, loadVersions]);

  const set = (patch) => setConfig((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveConfig(orgId, agent.id, config);
      setConfig(result.config);
      setMeta((m) => ({ ...m, has_unpublished_changes: true }));
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await saveConfig(orgId, agent.id, config);
      await publishConfig(orgId, agent.id, 'Configuration published from the dashboard');
      toast.success('Configuration published — live traffic now uses it');
      await Promise.all([load(), loadVersions(), refreshAgents()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleRestore = async (version) => {
    try {
      const result = await restoreConfigVersion(orgId, agent.id, version);
      setConfig(result.config);
      setMeta((m) => ({ ...m, has_unpublished_changes: true }));
      toast.success(`Version ${version} loaded into the draft — publish to make it live`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore that version');
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading configuration…
      </div>
    );
  }

  const threshold = config.confidence_threshold ?? 75;
  const escalate = config.escalation_trigger ?? 40;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-[10px]">
            v{meta.version}
          </Badge>
          {meta.has_unpublished_changes ? (
            <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-0">
              Unpublished changes
            </Badge>
          ) : (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0">
              Published
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-2" />
            )}
            Save Draft
          </Button>
          <Button size="sm" onClick={handlePublish} disabled={publishing}>
            {publishing ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-2" />
            )}
            Publish Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-10">
          {/* Identity */}
          <Section title="Agent Identity">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Display name">
                    <Input
                      value={config.display_name || ''}
                      onChange={(e) => set({ display_name: e.target.value })}
                      className="h-9"
                    />
                  </Field>
                  <Field label="Avatar URL">
                    <Input
                      value={config.avatar_url || ''}
                      placeholder="https://…"
                      onChange={(e) => set({ avatar_url: e.target.value })}
                      className="h-9"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Agent language">
                    <Select
                      value={config.language_mode || 'bilingual'}
                      onValueChange={(v) => set({ language_mode: v })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English only</SelectItem>
                        <SelectItem value="ar">Arabic only</SelectItem>
                        <SelectItem value="bilingual">Bilingual (EN/AR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Response language">
                    <RadioGroup
                      value={config.response_language_mode || 'match'}
                      onValueChange={(v) => set({ response_language_mode: v })}
                      className="flex flex-col gap-2 pt-1"
                    >
                      {[
                        { v: 'match', l: "Match the user's language" },
                        { v: 'en', l: 'Always English' },
                        { v: 'ar', l: 'Always Arabic' },
                      ].map((opt) => (
                        <div key={opt.v} className="flex items-center gap-2">
                          <RadioGroupItem value={opt.v} id={`lang-${opt.v}`} />
                          <Label htmlFor={`lang-${opt.v}`} className="text-xs font-normal cursor-pointer">
                            {opt.l}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </Field>
                </div>
              </div>

              <Field
                label="Persona description"
                hint="Sets the model's baseline behaviour before the system prompt is applied."
              >
                <Textarea
                  className="min-h-[160px] text-sm leading-relaxed bg-muted/20"
                  value={config.persona_description || ''}
                  onChange={(e) => set({ persona_description: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Separator />

          {/* System prompt */}
          <Section title="System Prompt">
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => set({ system_prompt: '' })}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear
                </Button>
                <span
                  className={cn(
                    'text-xs font-medium',
                    (config.system_prompt || '').length > PROMPT_CHAR_LIMIT
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  )}
                >
                  {(config.system_prompt || '').length.toLocaleString()} /{' '}
                  {PROMPT_CHAR_LIMIT.toLocaleString()} characters
                </span>
              </div>
              <Textarea
                value={config.system_prompt || ''}
                onChange={(e) => set({ system_prompt: e.target.value })}
                className="min-h-[380px] font-mono text-sm leading-relaxed resize-none"
                placeholder="Enter your system prompt…"
              />
              <p className="text-[11px] text-muted-foreground italic">
                Retrieved document chunks are appended automatically under a [CONTEXT] block at
                request time — you do not need to add them here.
              </p>
            </div>
          </Section>

          <Separator />

          {/* Tone & thresholds */}
          <Section title="Tone & Behaviour">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-4">
                <Field label="Tone style">
                  <SegmentedControl
                    options={TONES}
                    value={config.tone || 'Balanced'}
                    onChange={(v) => set({ tone: v })}
                  />
                </Field>
                <Field label="Response length">
                  <SegmentedControl
                    options={LENGTHS}
                    value={config.response_length || 'Detailed'}
                    onChange={(v) => set({ response_length: v })}
                  />
                </Field>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Confidence threshold</Label>
                    <span className="text-xs font-mono font-bold text-primary">{threshold}%</span>
                  </div>
                  <Slider
                    value={[threshold]}
                    onValueChange={([v]) => set({ confidence_threshold: v })}
                    max={100}
                    step={1}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Below this, the agent must cite its sources rather than answer freely.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Escalation trigger</Label>
                    <span className="text-xs font-mono font-bold text-primary">{escalate}%</span>
                  </div>
                  <Slider
                    value={[escalate]}
                    onValueChange={([v]) => set({ escalation_trigger: v })}
                    max={100}
                    step={1}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Below this, the agent stops guessing and offers a human handoff.
                  </p>
                </div>

                {escalate > threshold && (
                  <p className="text-[11px] text-destructive flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                    The escalation trigger must sit at or below the confidence threshold.
                  </p>
                )}

                <div className="pt-1">
                  <ConfidenceBar
                    value={threshold}
                    threshold={threshold}
                    escalateAt={escalate}
                    showLabel={false}
                  />
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Ticks mark where the agent switches from escalating, to citing, to
                    answering freely.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Field label="Greeting message">
                  <Input
                    className="text-sm h-9"
                    value={config.greeting_message || ''}
                    onChange={(e) => set({ greeting_message: e.target.value })}
                  />
                </Field>
                <Field label="Fallback message" hint="Sent when confidence drops below the trigger.">
                  <Textarea
                    className="text-sm min-h-[90px]"
                    value={config.fallback_message || ''}
                    onChange={(e) => set({ fallback_message: e.target.value })}
                  />
                </Field>
              </div>
            </div>
          </Section>

          <Separator />

          {/* Response rules */}
          <Section title="Response Rules">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {RULES.map((rule) => {
                const checked = Boolean((config.response_rules || {})[rule.key]);
                return (
                  <button
                    key={rule.key}
                    type="button"
                    onClick={() =>
                      set({
                        response_rules: { ...(config.response_rules || {}), [rule.key]: !checked },
                      })
                    }
                    className={cn(
                      'p-4 rounded-xl border text-left transition-all',
                      checked
                        ? 'bg-primary/[0.03] border-primary/30 shadow-sm'
                        : 'bg-card border-border hover:border-muted-foreground/30'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox checked={checked} className="mt-1 pointer-events-none" />
                      <div className="space-y-1">
                        <span className="text-sm font-semibold block">{rule.title}</span>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          {rule.desc}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Version history */}
          <div className="border border-border rounded-xl overflow-hidden bg-card">
            <button
              onClick={() => setVersionsOpen((v) => !v)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <History className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold">Version History</h3>
                  <p className="text-xs text-muted-foreground">
                    {versions.length} published version{versions.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              {versionsOpen ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>

            {versionsOpen && (
              <div className="border-t border-border">
                {versions.length === 0 ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">
                    Nothing published yet.
                  </p>
                ) : (
                  <Table>
                    <TableHeader className="bg-muted/30">
                      <TableRow>
                        <TableHead className="w-[110px]">Version</TableHead>
                        <TableHead>Summary</TableHead>
                        <TableHead>Published by</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {versions.map((v) => (
                        <TableRow key={v.version} className={cn(v.is_current && 'bg-primary/5')}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              v{v.version}
                              {v.is_current && (
                                <span className="px-1.5 py-0.5 rounded-full bg-primary/20 text-[10px] text-primary font-bold">
                                  LIVE
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-xs">
                            {v.summary}
                          </TableCell>
                          <TableCell className="text-xs">{v.published_by_name || '—'}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {formatWhen(v.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            {!v.is_current && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 gap-1.5 text-xs"
                                onClick={() => handleRestore(v.version)}
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Restore
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Live preview */}
        <aside className="lg:col-span-4 lg:sticky lg:top-6 space-y-6">
          <LivePreview orgId={orgId} agent={agent} config={config} />
        </aside>
      </div>
    </div>
  );
};

const LivePreview = ({ orgId, agent, config }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const result = await sendChatMessage(orgId, agent.id, {
        message: text,
        session_id: sessionId,
        channel: 'text',
      });
      setSessionId(result.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.message.content,
          confidence: result.confidence,
          sources: result.sources,
          escalated: result.should_escalate,
        },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'The agent could not respond';
      toast.error(detail);
      setMessages((prev) => [...prev, { role: 'error', content: detail }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col bg-card border border-border rounded-xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <h3 className="text-sm font-semibold">Live Preview</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            setMessages([]);
            setSessionId(null);
          }}
        >
          Reset
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[320px] max-h-[520px] agent-scrollbar">
        {messages.length === 0 && (
          <div className="text-center py-10 space-y-2">
            <Bot className="w-8 h-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground px-4">
              {config.greeting_message || 'Ask something to test this configuration.'}
            </p>
            <p className="text-[11px] text-muted-foreground/70">
              Uses your unsaved draft, so you can test before publishing.
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn('flex gap-3', msg.role === 'user' && 'flex-row-reverse')}>
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
                msg.role === 'user' ? 'bg-muted' : 'bg-primary/10 text-primary'
              )}
            >
              {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>
            <div className="space-y-2 max-w-[85%]">
              <div
                className={cn(
                  'p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap',
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-tr-none'
                    : msg.role === 'error'
                    ? 'bg-destructive/10 text-destructive border border-destructive/20'
                    : 'bg-muted/50 border border-border rounded-tl-none'
                )}
                dir={/[؀-ۿ]/.test(msg.content) ? 'rtl' : 'ltr'}
              >
                {msg.content}
              </div>

              {msg.role === 'assistant' && (
                <div className="space-y-2">
                  <ConfidenceBar
                    value={msg.confidence}
                    threshold={config.confidence_threshold ?? 75}
                    escalateAt={config.escalation_trigger ?? 40}
                  />
                  {msg.escalated && (
                    <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/30">
                      Escalation offered
                    </Badge>
                  )}
                  {msg.sources?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {msg.sources.slice(0, 3).map((s) => (
                        <Badge key={s.doc_id} variant="secondary" className="text-[10px] font-normal">
                          {s.title}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {config.display_name || 'The agent'} is thinking…
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Test your prompt…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            className="h-9 text-sm"
          />
          <Button size="sm" className="h-9 w-9 p-0" onClick={send} disabled={sending}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {agent.space_ids?.length === 0 && (
          <div className="flex items-start gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-md">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-700 dark:text-amber-400">
              No spaces are connected, so every answer will fall back. Connect sources first.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="space-y-6">
    <div className="flex items-center gap-2">
      <div className="h-1 w-1 rounded-full bg-primary" />
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{title}</h2>
    </div>
    {children}
  </section>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

const SegmentedControl = ({ options, value, onChange }) => (
  <div className="flex p-1 bg-muted rounded-lg w-full">
    {options.map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange(option)}
        className={cn(
          'flex-1 py-1.5 text-xs font-medium rounded-md transition-all',
          value === option
            ? 'bg-card text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        {option}
      </button>
    ))}
  </div>
);

export default PromptPersonaPage;
